import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Search, ChevronDown, ChevronUp, RefreshCw, Trash2, UserCheck,
  CalendarCheck, FileCheck, Building2, Ruler, MessageCircle, CheckCircle2,
  Clock, AlertCircle, Loader2,
} from 'lucide-react';
import { ordersApi } from '@/services/api';
import { toast } from 'sonner';

const STEP_LABELS = ['', 'Measurements', 'Tech Visit', 'Showroom', 'Agreement', 'Production', 'Installation'];
const PRODUCTION_STAGES = ['pending', 'materials_prep', 'manufacturing', 'quality_check', 'ready'];
const PRODUCTION_LABELS = {
  pending: 'Pending',
  materials_prep: 'Materials Prep',
  manufacturing: 'Manufacturing',
  quality_check: 'Quality Check',
  ready: 'Ready',
};
const STATUS_CLASSES = {
  active: 'bg-amber-50 text-amber-700 border border-amber-200',
  completed: 'bg-green-50 text-green-700 border border-green-200',
  cancelled: 'bg-red-50 text-red-600 border border-red-200',
};
const STEP_COLORS = [
  '', 'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-amber-500', 'bg-orange-500', 'bg-emerald-500',
];

const inputCls = 'px-3 py-2 border border-white/15 bg-white/5 text-white text-sm focus:outline-none focus:border-gold transition-colors';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStep, setFilterStep] = useState('');
  const [search, setSearch] = useState('');
  const [notifications, setNotifications] = useState({});
  const [stats, setStats] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterStep) params.current_step = parseInt(filterStep);
      const res = await ordersApi.getAll(params);
      setOrders(res.data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterStep]);

  const loadStats = useCallback(async () => {
    try {
      const res = await ordersApi.getOrderStats();
      setStats(res.data);
    } catch {/* non-blocking */}
  }, []);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [loadOrders, loadStats]);

  const loadNotifications = async (orderId) => {
    if (notifications[orderId]) return;
    try {
      const res = await ordersApi.getNotifications(orderId);
      setNotifications((prev) => ({ ...prev, [orderId]: res.data }));
    } catch {/* ignore */}
  };

  const handleExpand = (id) => {
    const newId = expandedId === id ? null : id;
    setExpandedId(newId);
    if (newId) loadNotifications(newId);
  };

  const handleEditStart = (order) => {
    setEditingId(order.id);
    setEditForm({
      current_step: order.current_step,
      status: order.status,
      technician_assigned: order.technician_assigned || '',
      admin_notes: order.admin_notes || '',
      production_status: order.production?.status || 'pending',
      production_notes: order.production?.notes || '',
      agreement_design: order.agreement?.design_details || '',
      agreement_price: order.agreement?.total_price || '',
    });
  };

  const handleEditSave = async (order) => {
    try {
      const updatePayload = {
        current_step: parseInt(editForm.current_step),
        status: editForm.status || undefined,
        technician_assigned: editForm.technician_assigned || undefined,
        admin_notes: editForm.admin_notes || undefined,
        production: {
          status: editForm.production_status || 'pending',
          notes: editForm.production_notes || undefined,
          updated_at: new Date().toISOString(),
        },
        agreement: {
          design_details: editForm.agreement_design || order.agreement?.design_details,
          total_price: editForm.agreement_price ? parseFloat(editForm.agreement_price) : order.agreement?.total_price,
          accepted: order.agreement?.accepted || false,
          signed_at: order.agreement?.signed_at,
          notes: order.agreement?.notes,
        },
      };

      const res = await ordersApi.updateAdmin(order.id, updatePayload);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? res.data : o)));
      setEditingId(null);
      toast.success('Order updated');
      loadStats();
    } catch {
      toast.error('Failed to update order');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await ordersApi.deleteAdmin(id);
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success('Order deleted');
      loadStats();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.customer_phone?.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Kitchen Orders</h1>
          <p className="text-white/50 text-sm mt-0.5">Manage and track all customer kitchen orders</p>
        </div>
        <button
          onClick={() => { loadOrders(); loadStats(); }}
          className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Orders', value: stats.total, color: 'text-white' },
            { label: 'Active', value: stats.active, color: 'text-amber-400' },
            { label: 'Completed', value: stats.completed, color: 'text-emerald-400' },
            { label: 'In Production', value: (stats.by_step?.['5'] || 0), color: 'text-orange-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 border border-white/10 p-4">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 flex-1">
          <Search size={15} className="text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, order #..."
            className="bg-transparent text-white text-sm py-2 flex-1 focus:outline-none placeholder:text-white/30"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={inputCls}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filterStep}
          onChange={(e) => setFilterStep(e.target.value)}
          className={inputCls}
        >
          <option value="">All Steps</option>
          {STEP_LABELS.slice(1).map((label, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}. {label}</option>
          ))}
        </select>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <Package size={40} className="mx-auto mb-4 opacity-30" />
          <p>No orders found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => {
            const isExpanded = expandedId === order.id;
            const isEditing = editingId === order.id;

            return (
              <div
                key={order.id}
                className="bg-white/4 border border-white/10 hover:border-white/20 transition-colors"
              >
                {/* Order Row */}
                <div
                  className="flex flex-col md:flex-row md:items-center gap-4 p-5 cursor-pointer"
                  onClick={() => handleExpand(order.id)}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Step Badge */}
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        STEP_COLORS[order.current_step] || 'bg-white/20'
                      }`}
                    >
                      <span className="text-white text-xs font-bold">{order.current_step}</span>
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold" dir="ltr">{order.order_number}</span>
                        <span
                          className={`text-xs px-2 py-0.5 font-medium ${STATUS_CLASSES[order.status] || STATUS_CLASSES.active}`}
                        >
                          {order.status}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm truncate">{order.customer_name}</p>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center gap-6 text-sm text-white/50 flex-shrink-0">
                    <span>{order.customer_phone}</span>
                    <span>{STEP_LABELS[order.current_step]}</span>
                    <span>{formatDate(order.created_at)}</span>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <a
                      href={`https://wa.me/${order.customer_phone?.replace(/[^\d+]/g, '')}?text=${encodeURIComponent(`مرحباً ${order.customer_name}، طلبك ${order.order_number}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#25D366] hover:opacity-70 transition-opacity"
                      title="WhatsApp"
                    >
                      <MessageCircle size={18} />
                    </a>
                    <button
                      onClick={() => handleDelete(order.id)}
                      className="text-white/30 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                    {isExpanded ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-white/8 p-5 space-y-6">
                    {/* Customer Info */}
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-white/40 mb-1">Customer</p>
                        <p className="text-white font-medium">{order.customer_name}</p>
                        <p className="text-white/60">{order.customer_phone}</p>
                        <p className="text-white/50 text-xs mt-1">{order.customer_address}</p>
                      </div>
                      {order.measurements && (
                        <div>
                          <p className="text-white/40 mb-1">Measurements</p>
                          {order.measurements.length && (
                            <p className="text-white" dir="ltr">
                              {order.measurements.length} × {order.measurements.width} × {order.measurements.height} cm
                            </p>
                          )}
                          {order.measurements.notes && (
                            <p className="text-white/50 text-xs mt-1">{order.measurements.notes}</p>
                          )}
                          {order.measurements.plan_file && (
                            <a href={order.measurements.plan_file} target="_blank" rel="noopener noreferrer" className="text-gold text-xs hover:underline">View Plan</a>
                          )}
                        </div>
                      )}
                      <div>
                        <p className="text-white/40 mb-1">Timeline</p>
                        {order.technician_visit?.date && (
                          <p className="text-white/60 text-xs flex items-center gap-1"><UserCheck size={11} /> Tech: {order.technician_visit.date}</p>
                        )}
                        {order.showroom_visit?.date && (
                          <p className="text-white/60 text-xs flex items-center gap-1"><Building2 size={11} /> Showroom: {order.showroom_visit.date}</p>
                        )}
                        {order.agreement?.signed_at && (
                          <p className="text-white/60 text-xs flex items-center gap-1"><FileCheck size={11} /> Signed: {formatDate(order.agreement.signed_at)}</p>
                        )}
                        {order.installation?.date && (
                          <p className="text-white/60 text-xs flex items-center gap-1"><CalendarCheck size={11} /> Install: {order.installation.date}</p>
                        )}
                      </div>
                    </div>

                    {/* Edit Form */}
                    {!isEditing ? (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEditStart(order)}
                          className="px-5 py-2 bg-gold text-charcoal-deep text-sm font-bold hover:opacity-90 transition-opacity"
                        >
                          Edit Order
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white/4 border border-white/10 p-5 space-y-5">
                        <p className="text-white/60 text-sm font-medium uppercase tracking-widest">Edit Order</p>

                        <div className="grid md:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Current Step</label>
                            <select
                              value={editForm.current_step}
                              onChange={(e) => setEditForm((p) => ({ ...p, current_step: e.target.value }))}
                              className={`${inputCls} w-full`}
                            >
                              {STEP_LABELS.slice(1).map((label, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}. {label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Status</label>
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                              className={`${inputCls} w-full`}
                            >
                              <option value="active">Active</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Assigned Technician</label>
                            <input
                              type="text"
                              value={editForm.technician_assigned}
                              onChange={(e) => setEditForm((p) => ({ ...p, technician_assigned: e.target.value }))}
                              className={`${inputCls} w-full`}
                              placeholder="Technician name"
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Production Status</label>
                            <select
                              value={editForm.production_status}
                              onChange={(e) => setEditForm((p) => ({ ...p, production_status: e.target.value }))}
                              className={`${inputCls} w-full`}
                            >
                              {PRODUCTION_STAGES.map((s) => (
                                <option key={s} value={s}>{PRODUCTION_LABELS[s]}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Design Details</label>
                            <input
                              type="text"
                              value={editForm.agreement_design}
                              onChange={(e) => setEditForm((p) => ({ ...p, agreement_design: e.target.value }))}
                              className={`${inputCls} w-full`}
                              placeholder="Design description..."
                            />
                          </div>
                          <div>
                            <label className="block text-white/50 text-xs mb-1.5">Total Price (SAR)</label>
                            <input
                              type="number"
                              value={editForm.agreement_price}
                              onChange={(e) => setEditForm((p) => ({ ...p, agreement_price: e.target.value }))}
                              className={`${inputCls} w-full`}
                              placeholder="e.g. 35000"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-white/50 text-xs mb-1.5">Admin Notes (shown to customer)</label>
                          <textarea
                            value={editForm.admin_notes}
                            onChange={(e) => setEditForm((p) => ({ ...p, admin_notes: e.target.value }))}
                            rows={3}
                            className={`${inputCls} w-full resize-none`}
                            placeholder="Notes visible to the customer..."
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => handleEditSave(order)}
                            className="px-6 py-2.5 bg-gold text-charcoal-deep text-sm font-bold hover:opacity-90 transition-opacity"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-6 py-2.5 text-white/60 hover:text-white text-sm transition-colors border border-white/10 hover:border-white/20"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Notification Logs */}
                    {notifications[order.id] && notifications[order.id].length > 0 && (
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-widest mb-3">WhatsApp Log</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {notifications[order.id].map((log, i) => (
                            <div key={i} className="flex items-start gap-3 text-xs bg-white/3 p-3 border border-white/5">
                              <MessageCircle size={13} className="text-[#25D366] flex-shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-white/60 font-medium">{log.message_type}</span>
                                  <span className="text-white/30">{formatDate(log.sent_at)}</span>
                                </div>
                                <p className="text-white/50 truncate">{log.message_body}</p>
                                {log.wa_link && (
                                  <a href={log.wa_link} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                                    Open WhatsApp
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
